// A dropdown in the palette colours, used instead of the native <select>. The list
// of a native select is drawn by the browser (Firefox ignores the page colours
// there), so it never matched the rest of the app.
//
// appearance "pill": small rounded button, for column headers.
// appearance "field": full-width field, for settings.

import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";

import classNames from "classnames";

import { Icon } from "flavours/glitch/components/icon";

export const SelectMenu = ({ id, value, options, onChange, disabled, appearance = "field", title, className }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const current = options.find(option => option.value === value) || options[0];

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        rootRef.current?.querySelector(".select-menu__button")?.focus();
      }
    };

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    document.addEventListener("keydown", handleKey);
    // Focus the current entry so the arrow keys and Enter work right away.
    rootRef.current?.querySelector("[aria-checked='true']")?.focus();

    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const handleToggle = useCallback(() => setOpen(state => !state), []);

  const handleSelect = useCallback((e) => {
    setOpen(false);
    rootRef.current?.querySelector(".select-menu__button")?.focus();
    onChange(e.currentTarget.dataset.value);
  }, [onChange]);

  const handleMenuKey = useCallback((e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") {
      return;
    }
    e.preventDefault();
    const items = [...rootRef.current.querySelectorAll("[role='menuitemradio']")];
    const index = items.indexOf(document.activeElement);
    items[(index + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length]?.focus();
  }, []);

  return (
    <span className={classNames("select-menu", `select-menu--${appearance}`, className)} ref={rootRef}>
      <button
        id={id}
        type='button'
        className='select-menu__button'
        onClick={handleToggle}
        disabled={disabled}
        aria-haspopup='menu'
        aria-expanded={open}
        title={title}
      >
        <span className='select-menu__value'>{current?.label}</span>
        <Icon id='caret-down' className='select-menu__caret' />
      </button>

      {open && (
        <span className='select-menu__list' role='menu' tabIndex={-1} aria-label={title} onKeyDown={handleMenuKey}>
          {options.map(option => (
            <button
              key={option.value}
              type='button'
              role='menuitemradio'
              aria-checked={option === current}
              data-value={option.value}
              onClick={handleSelect}
            >
              {option.label}
            </button>
          ))}
        </span>
      )}
    </span>
  );
};

SelectMenu.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
  })).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  appearance: PropTypes.oneOf(["pill", "field"]),
  title: PropTypes.string,
  className: PropTypes.string,
};
